<template>
  <div
    :class="
      classes(
        n(),
      )
    "
    role="group"
  >
    <!-- 使用 options 渲染 -->
    <template v-if="options && options.length">
      <Checkbox
        v-for="(option, index) in normalizedOptions"
        :key="index"
        :checkbox-value="getOptionValue(option)"
        :label="getOptionLabel(option)"
        :desc="getOptionDesc(option)"
        :disabled="disabled || getOptionDisabled(option)"
      />
    </template>
    <!-- 使用 slot 渲染 -->
    <template v-else>
      <slot />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, provide } from 'virtual-vue';
import { checkboxGroupProps } from './checkbox-group-props';
import { emits, useCheckboxGroupEmits } from './emits';
import { createNamespace } from '../utils/components';
import Checkbox from './checkbox.vue';
import type { CheckboxOption, CheckboxOptionObj } from './checkbox';
import { CHECKBOX_GROUP_KEY } from './constants';
import { useVModel } from '../hooks/use-v-model';

const { name, n, classes } = createNamespace('checkbox-group');

export default defineComponent({
  name,
  components: {
    Checkbox,
  },
  props: checkboxGroupProps,
  emits,
  setup(props) {
    // 使用 useVModel 管理选中值
    const innerValue = useVModel(props);
    const emit = useCheckboxGroupEmits();

    // 标准化 options
    const normalizedOptions = computed(() => {
      if (!props.options) return [];
      return props.options.map((option) => {
        if (typeof option === 'string' || typeof option === 'number') {
          return {
            label: String(option),
            value: option,
          };
        }
        return option;
      });
    });

    // 获取选项值
    const getOptionValue = (option: CheckboxOption | CheckboxOptionObj) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return option.value;
    };

    // 获取选项标签
    const getOptionLabel = (option: CheckboxOption | CheckboxOptionObj) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return String(option);
      }
      return option.label;
    };

    // 获取选项描述
    const getOptionDesc = (option: CheckboxOption | CheckboxOptionObj) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return undefined;
      }
      return option.desc;
    };

    // 获取选项禁用状态
    const getOptionDisabled = (option: CheckboxOption | CheckboxOptionObj) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return false;
      }
      return option.disabled || false;
    };

    // Checkbox 变化处理
    const handleCheckboxChange = (newValue: Array<string | number | boolean>, context?: { e: Event }) => {
      innerValue.value = newValue;  // 触发 v-model 更新
      emit('change', newValue, context || { e: new Event('change') });
    };

    // 提供给子组件
    provide(CHECKBOX_GROUP_KEY, {
      value: innerValue,
      disabled: computed(() => props.disabled),
      onChange: handleCheckboxChange,
    });

    return {
      n,
      classes,
      innerValue,
      normalizedOptions,
      getOptionValue,
      getOptionLabel,
      getOptionDesc,
      getOptionDisabled,
      handleCheckboxChange,
    };
  },
});
</script>

